import { supabase } from '../lib/supabase';

interface PaymentGatewayConfig {
  cinetpay_enabled: boolean;
  lygos_enabled: boolean;
  alternative_url: string;
  deletion_price: {
    amount: number;
    currency: string;
  };
}

/**
 * Récupère la configuration des passerelles de paiement depuis Supabase
 */
export const getPaymentGatewayConfig = async (): Promise<PaymentGatewayConfig> => {
  try {
    // Récupérer la configuration depuis Supabase
    const [paymentConfig, deletionPrice] = await Promise.all([
      supabase.rpc('get_app_setting', { setting_key: 'payment_gateway' }),
      supabase.rpc('get_app_setting', { setting_key: 'relation_deletion_price' })
    ]);

    // Log pour le débogage
    console.log('Configuration de paiement reçue:', paymentConfig.data);

    if (paymentConfig.error) {
      console.error('Erreur lors de la récupération de la configuration de paiement:', paymentConfig.error);
      throw new Error('Erreur lors de la récupération de la configuration de paiement');
    }

    if (!paymentConfig.data) {
      console.error('Aucune configuration de paiement trouvée dans Supabase');
      throw new Error('Configuration de paiement non trouvée');
    }

    // Vérifier que la structure de la configuration est correcte
    const config = paymentConfig.data as PaymentGatewayConfig;
    if (!config.alternative_url) {
      console.error('URL alternative manquante dans la configuration');
      throw new Error('Configuration de paiement invalide');
    }

    return {
      cinetpay_enabled: config.cinetpay_enabled ?? false,
      lygos_enabled: config.lygos_enabled ?? false,
      alternative_url: config.alternative_url,
      deletion_price: deletionPrice.data || { amount: 500, currency: 'XAF' }
    };
  } catch (error) {
    console.error('Exception lors de la récupération de la configuration de paiement:', error);
    // En cas d'erreur, retourner une configuration par défaut sécurisée
    return {
      cinetpay_enabled: false,
      lygos_enabled: false,
      alternative_url: 'https://pay.lygosapp.com/$bodycount',
      deletion_price: { amount: 500, currency: 'XAF' }
    };
  }
};

export const getDeletionPrice = async (): Promise<{ amount: number; currency: string }> => {
  const config = await getPaymentGatewayConfig();
  return config.deletion_price;
};

/**
 * Vérifie si CinetPay est activé
 * @returns true si CinetPay est activé, false sinon
 */
export const checkCinetPayEnabled = async (): Promise<boolean> => {
  const config = await getPaymentGatewayConfig();
  return config.cinetpay_enabled;
};

/**
 * Vérifie si Lygos est activé
 * @returns true si Lygos est activé, false sinon
 */
export const checkLygosEnabled = async (): Promise<boolean> => {
  const config = await getPaymentGatewayConfig();
  return config.lygos_enabled;
};

/**
 * Détermine l'URL de paiement à utiliser en fonction de la configuration
 * @param relationId ID de la relation pour laquelle le paiement est effectué
 * @param amount Montant du paiement
 * @param description Description du paiement
 */
export const getPaymentUrl = async (
  relationId: string,
  amount: number,
  description: string
): Promise<{ url: string | null; price: { amount: number; currency: string } }> => {
  try {
    const config = await getPaymentGatewayConfig();
    
    // Vérifier si Lygos est activé
    if (config.lygos_enabled) {
      return {
        url: null,
        price: config.deletion_price
      }; // Retourner null + prix pour utiliser le processus de paiement Lygos
    }

    // Vérifier si CinetPay est activé
    if (config.cinetpay_enabled) {
      return {
        url: null,
        price: config.deletion_price
      }; // Retourner null + prix pour utiliser le processus de paiement CinetPay
    }

    // Si les deux services sont désactivés, utiliser l'URL alternative telle quelle
    if (!config.lygos_enabled && !config.cinetpay_enabled) {
      return {
        url: config.alternative_url,
        price: config.deletion_price
      };
    }

    // Par défaut, retourner null avec le prix
    return {
      url: null,
      price: config.deletion_price
    };
  } catch (error) {
    console.error('Erreur lors de la vérification des services de paiement:', error);
    return {
      url: `https://bodycount.app/donate?amount=${amount}&description=${encodeURIComponent(description)}&relationId=${relationId}`,
      price: { amount: 500, currency: 'XAF' }
    };
  }
}; 